/**
 * Component: Button Mirror
 * 
 * Mirrors another button's textContent and disabled state, and forwards clicks.
 * 
 * Usage:
 * Add data-mirror-selector=".target-selector" to a button element.
 * The button will mirror the target's textContent and aria-disabled state.
 * Clicking the mirror button will trigger a click on the target button.
 */

(function() {
  'use strict';

  class ButtonMirror {
    constructor(button) {
      this.button = button;
      this.selector = button.getAttribute('data-mirror-selector');
      this.target = null;
      this.observer = null;
      this.targetObserver = null;
      
      if (!this.selector) {
        console.warn('ButtonMirror: No selector provided');
        return;
      }
      
      this.init();
    }

    init() {
      // Find target element
      this.findTarget();
      
      if (!this.target) {
        // Target not found yet, watch for it
        this.watchForTarget();
        return;
      }
      
      // Set up mirroring
      this.setupMirroring();
      this.setupClickForwarding();
    }

    findTarget() {
      try {
        this.target = document.querySelector(this.selector);
      } catch (e) {
        console.error('ButtonMirror: Invalid selector', this.selector, e);
      }
    }

    watchForTarget() {
      // Use MutationObserver to watch for target element
      const observer = new MutationObserver(() => {
        this.findTarget();
        if (this.target) {
          observer.disconnect();
          this.setupMirroring();
          this.setupClickForwarding();
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Also check periodically as fallback
      const checkInterval = setInterval(() => {
        this.findTarget();
        if (this.target) {
          clearInterval(checkInterval);
          observer.disconnect();
          this.setupMirroring();
          this.setupClickForwarding();
        }
      }, 100);
      
      // Clean up after 10 seconds if target still not found
      setTimeout(() => {
        clearInterval(checkInterval);
        observer.disconnect();
        if (!this.target) {
          console.warn('ButtonMirror: Target not found after 10 seconds', this.selector);
        }
      }, 10000);
    }

    setupMirroring() {
      if (!this.target) return;
      
      // Initial sync
      this.syncFromTarget();
      
      // Watch for changes to target's textContent and aria-disabled
      this.targetObserver = new MutationObserver(() => {
        this.syncFromTarget();
      });
      
      this.targetObserver.observe(this.target, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['aria-disabled']
      });
      
      // Also listen for attribute changes on the target
      const attributeObserver = new MutationObserver(() => {
        this.syncFromTarget();
      });
      
      attributeObserver.observe(this.target, {
        attributes: true,
        attributeFilter: ['aria-disabled', 'disabled']
      });
    }

    syncFromTarget() {
      if (!this.target) return;
      
      // Sync textContent - get the text from target button
      const targetText = this.target.textContent || this.target.innerText || '';
      this.button.textContent = targetText;
      
      // Sync disabled state from aria-disabled
      // Convert aria-disabled="true" to disabled attribute
      const ariaDisabled = this.target.getAttribute('aria-disabled');
      const isDisabled = ariaDisabled === 'true' || this.target.disabled;
      
      if (isDisabled) {
        this.button.disabled = true;
      } else {
        this.button.disabled = false;
      }
    }

    setupClickForwarding() {
      if (!this.target) return;
      
      this.button.addEventListener('click', (e) => {
        // Only forward if target exists and is not disabled
        if (this.target && !this.target.disabled && this.target.getAttribute('aria-disabled') !== 'true') {
          // Trigger click on target
          this.target.click();
        }
      });
    }

    destroy() {
      if (this.targetObserver) {
        this.targetObserver.disconnect();
      }
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  // Initialize all mirror buttons on page load
  function initMirrorButtons() {
    const mirrorButtons = document.querySelectorAll('button[data-mirror-selector]');
    mirrorButtons.forEach(button => {
      // Skip if already initialized
      if (button._buttonMirror) return;
      
      button._buttonMirror = new ButtonMirror(button);
    });
  }

  // Watch for new mirror buttons added to DOM
  function watchNewMirrorButtons() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a mirror button
            if (node.matches && node.matches('button[data-mirror-selector]')) {
              if (!node._buttonMirror) {
                node._buttonMirror = new ButtonMirror(node);
              }
            }
            // Also check child buttons
            const childButtons = node.querySelectorAll ? node.querySelectorAll('button[data-mirror-selector]') : [];
            childButtons.forEach(button => {
              if (!button._buttonMirror) {
                button._buttonMirror = new ButtonMirror(button);
              }
            });
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initMirrorButtons();
      watchNewMirrorButtons();
    });
  } else {
    initMirrorButtons();
    watchNewMirrorButtons();
  }
})();
